import { concat, from, of, delay } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { normalizeUpdateAction } from '@banking/ui/lib/workflow';
import * as cardListWorkflow from './card-list';
import { CreateUserAuthorizationResponse, CreateUserAuthorizationRequest } from '@square/dashboard-protos/models/squareup/bizbank/card_management';
import { ExternalListBalanceInstrumentsRequest } from '@square/dashboard-protos/models/squareup/bankc/service';
import { GetBillPayDataRequest, GetBillPayDataResponse } from '@square/dashboard-protos/models/squareup/bbfrontend/v1/bill_pay';
import { BootstrapCreatePrintableCheckRequest, CreatePrintableCheckRequest, DownloadPrintableCheckRequest, PrintableCheckAccessRequest } from '@square/dashboard-protos/models/squareup/bbfrontend/common/printable_check/printable_check_requests';
import { StartTwoFactorAuthenticationRequest, TwoFactorAuthenticationRequestType } from '@square/dashboard-protos/models/squareup/bizbank/service';
import { BalanceInstrumentType, BalanceInstrumentState } from '@square/dashboard-protos/models/squareup/bankc/instruments';
import { assertNonNullable } from '@banking/ui/utils/assert';
import { nonNullable } from '@banking/ui/utils/predicates';
import { Action, ActionCreator, StateSelector, StateType } from './index';
import { get as getFromLocalStorage, set as setToLocalStorage } from '@square/dashboard-utilities/utils/persistable';
import { OneTrustConsent } from '@square/dashboard-utilities/utils/one-trust/one-trust-compliance';
import { Money } from '@square/dashboard-protos/models/squareup/common/currency';
import { downloadPdf, printPdf } from '@banking/ui/utils/pdf';
import { InputHintDescription } from '@square/dashboard-protos/models/squareup/bbfrontend/common/component/component';
import { GetCheckingDirectDepositAccountRequest } from '@square/dashboard-protos/models/squareup/bbfrontend/common/checking_direct_deposit_account/checking_direct_deposit_account';
export const HIDE_CHECK_EDUCATION_LOCAL_STORAGE_KEY = 'PrintableCheck.hasHiddenEducation';
const mapAuthStateResultToEnum = result => {
  switch (result) {
    case CreateUserAuthorizationResponse.Result.SUCCESS:
      return StateType.AuthState.SUCCESS;
    case CreateUserAuthorizationResponse.Result.INVALID_TOKEN:
      return StateType.AuthState.REJECTED;
    case CreateUserAuthorizationResponse.Result.EXPIRED_TOKEN:
      return StateType.AuthState.EXPIRED;
    case CreateUserAuthorizationResponse.Result.ERROR:
      return StateType.AuthState.ERROR;
    case CreateUserAuthorizationResponse.Result.DO_NOT_USE:
      throw new Error('received CreateUserAuthorizationResponse.Result.DO_NOT_USE');
  }
};

// Context
// =============================================================================

// Work
// =============================================================================

function bootstrapCreatePrintableCheck(context, state) {
  return context.bootstrapCreatePrintableCheck(BootstrapCreatePrintableCheckRequest.create({
    unitToken: state.unitToken
  })).then(ActionCreator.receivedBootstrapCreatePrintableCheck).catch(error => {
    context.sentry.captureException(error);
    return null;
  });
}
function createPrintableCheck(context, state) {
  return context.createPrintableCheck(CreatePrintableCheckRequest.create({
    amount: state.checkAmount,
    balanceId: state.balanceIds[0],
    idempotenceKey: state.checkIdempotenceKey,
    memo: state.checkMemo,
    payeeName: state.checkPayeeName
  })).then(ActionCreator.receivedCreatePrintableCheck).catch(error => {
    context.sentry.captureException(error);
    return null;
  });
}
function downloadPrintableCheck(context, state) {
  if (state.printableCheckId) {
    return context.downloadPrintableCheck(DownloadPrintableCheckRequest.create({
      printableCheckId: state.printableCheckId
    })).then(ActionCreator.receivedDownloadPrintableCheck).catch(error => {
      context.sentry.captureException(error);
      return null;
    });
  } else {
    context.sentry.captureException('DownloadPrintableCheck: no printable check id');
    return null;
  }
}
const fetchDirectDepositAccount = context => unitToken => {
  const instrumentType = BalanceInstrumentType.DIRECT_DEPOSIT_ACCOUNT;
  const request = ExternalListBalanceInstrumentsRequest.create({
    unitToken,
    instrumentType
  });
  const getBalanceInstruments = context.listBalanceInstruments(request).catch(error => {
    context.sentry.captureException(error);
    return null;
  });
  const receivedBalancesAction = from(getBalanceInstruments).pipe(filter(nonNullable), map(ActionCreator.receivedBalances));
  const toggleShowAccountNumber = from(getBalanceInstruments).pipe(map(response => {
    if (!response) {
      return ActionCreator.systemDisableToggle();
    }
    const directDepositInstrument = response.balanceInstrument.find(instrument => instrument.type === BalanceInstrumentType.DIRECT_DEPOSIT_ACCOUNT && instrument.state === BalanceInstrumentState.ENABLED);
    if (!directDepositInstrument?.directDepositAccount?.accountNumber) {
      return ActionCreator.systemDisableToggle();
    }
    return ActionCreator.systemToggleShowAccountNumber(response != null);
  }));
  return concat(receivedBalancesAction, toggleShowAccountNumber);
};
function fetchDirectDepositAccountV2(context, state) {
  const request = GetCheckingDirectDepositAccountRequest.create({
    unitToken: state.unitToken,
    fieldMask: {
      paths: ['data.routing_number', 'data.account_number_full', 'data.account_number_suffix']
    }
  });
  const getCheckingDirectDepositAccount = context.getCheckingDirectDepositAccount(request).then(ActionCreator.receivedDirectDepositAccount).catch(error => {
    context.sentry.captureException(error);
    return null;
  });
  const toggleShowAccountNumber = from(getCheckingDirectDepositAccount).pipe(map(response => {
    if (!response || !response.response.data?.accountNumberFull) {
      return ActionCreator.systemDisableToggle();
    }
    return ActionCreator.systemToggleShowAccountNumber(response != null);
  }));
  return concat(getCheckingDirectDepositAccount, toggleShowAccountNumber);
}
function getBillPayData(context, state) {
  return context.getBillPayData(GetBillPayDataRequest.create({
    unitToken: state.unitToken
  })).then(ActionCreator.receivedBillPayData).catch(error => {
    context.sentry.captureException(error);
    return null;
  });
}
function getPrintableCheckAccess(context, state) {
  return context.getPrintableCheckAccess(PrintableCheckAccessRequest.create({
    unitToken: state.unitToken
  })).then(ActionCreator.receivedPrintableCheckAccess).catch(error => {
    context.sentry.captureException(error);
    return null;
  });
}
const submitAuthorizationCode = createUserAuthorization => (unitToken, authenticationToken) => {
  const request = CreateUserAuthorizationRequest.create({
    unitToken,
    authenticationToken
  });
  return createUserAuthorization(request);
};

// Update
// =============================================================================

const handleBootstrapCreatePrintableCheck = context => state => {
  const work = () => from(bootstrapCreatePrintableCheck(context, state));
  return [state, work];
};
const handleCreatePrintableCheck = context => state => {
  const work = () => from(createPrintableCheck(context, state));
  return [state, work];
};
const handleDownloadPrintableCheck = context => (state, printableCheckId) => {
  if (state.checkPdf && state.checkPdfFilename && printableCheckId === state.printableCheckId) {
    downloadPdf(state.checkPdf, state.checkPdfFilename);
    return [{
      ...state,
      isLoadingDownloadCheck: false
    }, null];
  }
  const result = downloadPrintableCheck(context, state);
  if (result) {
    const work = () => from(result);
    return [{
      ...state,
      printableCheckId
    }, work];
  } else {
    return [state, null];
  }
};
const handleReceivedBillPayData = context => (state, action) => {
  const onPromptAuthComplete = success => success ? ActionCreator.userClickPayBills() : null;
  const unauthorizedErrors = [GetBillPayDataResponse.Error.UNAUTHORIZED, GetBillPayDataResponse.Error.AUTHORIZATION_FAILURE];
  if (!action.response.value) {
    if (unauthorizedErrors.includes(action.response?.error)) {
      return [{
        ...state,
        authIsActiveFor: StateType.AuthType.SHOW_BILL_PAY
      }, () => ActionCreator.systemPromptAuthCodeInput(onPromptAuthComplete)];
    } else {
      context.marketToasts.critical(context.translate('banking.balance.doMoreCard.error'));
      return [state, null];
    }
  }
  const billPayData = action.response.value;
  return [{
    ...state,
    billPayData,
    billPayModalActive: true
  }, null];
};
const handleReceivedBootstrapCreatePrintableCheck = context => (state, action) => {
  const response = assertNonNullable(action.response);
  return [{
    ...state,
    balanceIds: [...response.balanceIds],
    checkIdempotenceKey: context.getUUIDString(),
    educationConfiguration: response.createCheckConfiguration?.educationConfiguration,
    printableCheckModalActive: true,
    writeCheckConfiguration: response.createCheckConfiguration?.writeCheckConfiguration
  }, null];
};
function handleReceivedCreatePrintableCheck(state, action) {
  const response = assertNonNullable(action.response);
  if (response.success) {
    const {
      printableCheckId,
      configuration
    } = response.success;
    return [{
      ...state,
      checkIssuedConfiguration: configuration,
      isLoadingCreateCheck: false,
      printableCheckId,
      showCheckSuccess: true,
      showWriteACheck: false
    }, null];
  } else if (response.error) {
    const writeCheckConfiguration = response.error.configuration;
    const writeCheckInputHints = {
      amountInputHint: writeCheckConfiguration?.amountInput?.hint || state.writeCheckInputHints.amountInputHint,
      memoInputHint: writeCheckConfiguration?.memoInput?.description?.hint || state.writeCheckInputHints.memoInputHint,
      recipientInputHint: writeCheckConfiguration?.recipientInput?.description?.hint || state.writeCheckInputHints.recipientInputHint
    };
    return [{
      ...state,
      isLoadingCreateCheck: false,
      showWriteACheck: true,
      writeCheckConfiguration,
      writeCheckInputHints
    }, null];
  } else if (response.unrecoverableError) {
    return [{
      ...state,
      createPrintableCheckUnrecoverableError: response.unrecoverableError,
      isLoadingCreateCheck: false,
      showCheckError: true,
      showWriteACheck: false
    }, null];
  } else {
    return [state, null];
  }
}
const handleReceivedPrintableCheckAccess = context => (state, action) => {
  const response = assertNonNullable(action.response);
  const {
    allow,
    forbid
  } = response;
  if (allow) {
    if (allow.logEvents !== undefined && allow.logEvents.viewLogEvent !== undefined) {
      context.logEvents.track(allow.logEvents.viewLogEvent);
    }
    const work = state.showPrintableCheckModal ? () => of(ActionCreator.userClickOpenCreatePrintableCheck()) : null;
    return [{
      ...state,
      canSeePrintableCheck: true,
      printableCheckAccessConfiguration: allow
    }, work];
  } else if (forbid) {
    return [{
      ...state,
      canSeePrintableCheck: false
    }, null];
  } else {
    return [state, null];
  }
};
function handleReceivedDownloadPrintableCheck(state, action) {
  assertNonNullable(action.response);
  const {
    checkPdf,
    pdfFilename
  } = action.response;
  if (state.isLoadingDownloadCheck) {
    downloadPdf(checkPdf, pdfFilename);
  } else if (state.isLoadingPrintCheck) {
    printPdf(checkPdf);
  }
  return [{
    ...state,
    checkPdf,
    checkPdfFilename: pdfFilename,
    isLoadingDownloadCheck: false,
    isLoadingPrintCheck: false
  }, null];
}
const handleRequestBillPayData = context => state => {
  const work = () => from(getBillPayData(context, state));
  return [state, work];
};
const handleSystemReceivedAuthDecision = context => (state, action) => {
  const success = action.response.result === CreateUserAuthorizationResponse.Result.SUCCESS;
  const complete = state.onAuthAttemptComplete;
  const work = () => {
    if (state.authIsActiveFor === StateType.AuthType.SHOW_BILL_PAY && success) {
      return concat(of(ActionCreator.systemToggleAuthModal(false)), from(getBillPayData(context, state)));
    }
    return concat(of(ActionCreator.systemToggleAuthModal(!success)), normalizeUpdateAction(complete?.(success) ?? null));
  };
  return [{
    ...state,
    onAuthAttemptComplete: undefined,
    authStateResult: mapAuthStateResultToEnum(action.response.result)
  }, work];
};
const handleSystemRefreshRecentActivities = context => state => {
  context.refreshRecentActivities(state.unitToken);
  context.refreshBalances(state.unitToken);
  return [state, null];
};
const handleClickPayBills = context => state => {
  if (context.enabledNewBillPayIntegration) {
    return [state, ActionCreator.userClickPayBillsNew];
  } else {
    return [state, ActionCreator.requestBillPayData];
  }
};
const handleUserClickPrintCheck = context => (state, printableCheckId) => {
  const checkIssuedLogEvent = state.checkIssuedConfiguration?.logEvents?.clickPrintLogEvent;
  if (checkIssuedLogEvent) {
    context.logEvents.track(checkIssuedLogEvent);
  }
  if (state.checkPdf && printableCheckId === state.printableCheckId) {
    printPdf(state.checkPdf);
    return [{
      ...state,
      isLoadingPrintCheck: false
    }, null];
  }
  const work = () => of(ActionCreator.requestDownloadPrintableCheck(printableCheckId));
  return [{
    ...state,
    printableCheckId
  }, work];
};
const handleUserClickResendCode = context => state => {
  const work = () => {
    const toggleOff = of(ActionCreator.systemToggleResendButton(false));
    const toggleOn = of(ActionCreator.systemToggleResendButton(true)).pipe(delay(5000));
    if (state.authIsActiveFor === StateType.AuthType.SHOW_BILL_PAY) {
      return concat(toggleOff, from(getBillPayData(context, state)), toggleOn);
    } else {
      return concat(toggleOff, of(ActionCreator.userToggleAccountDetails()), toggleOn);
    }
  };
  return [state, work];
};
const handleUserClickSubmitAuthCode = context => state => {
  const work = () => submitAuthorizationCode(context.createUserAuthorization)(state.unitToken, state.authCode).then(ActionCreator.systemReceivedAuthDecision).catch(error => {
    context.sentry.captureException(error);
    return null;
  });
  return [state, work];
};
const handleUserToggleAccountDetails = context => state => {
  const toggle = !state.showAccountDetails;
  if (toggle === false) {
    return [state, () => ActionCreator.systemToggleShowAccountNumber(false)];
  }
  if (StateSelector.containsDirectDepositAccountNumber(state)) {
    return [state, () => ActionCreator.systemToggleShowAccountNumber(toggle)];
  }
  if (context.canBypass2FA) {
    const work = () => fetchDirectDepositAccountV2(context, state);
    return [state, work];
  }
  if (state.billPayData != null) {
    // FIXME(steckel) Check if direct deposit account information is here, if not then auth...
    const work = () => fetchDirectDepositAccount(context)(state.unitToken);
    return [state, work];
  }

  // but not authorized
  const requestType = TwoFactorAuthenticationRequestType.SHOW_DIRECT_DEPOSIT_ACCOUNT;
  const request = StartTwoFactorAuthenticationRequest.create({
    unitToken: state.unitToken,
    requestType
  });
  const work = () => {
    const onAuthCodeInputComplete = authorizationSuccess => authorizationSuccess ? fetchDirectDepositAccount(context)(state.unitToken) : ActionCreator.systemToggleShowAccountNumber(false);
    const twoFactorAuthentication = context.startTwoFactorAuthentication(request).then(response => response.success).catch(error => {
      context.sentry.captureException(error);
      return null;
    });
    return from(twoFactorAuthentication).pipe(map(requestedCode => {
      return requestedCode ? ActionCreator.systemPromptAuthCodeInput(onAuthCodeInputComplete) : ActionCreator.systemToggleShowAccountNumber(false);
    }));
  };
  return [{
    ...state,
    authIsActiveFor: StateType.AuthType.SHOW_ACCOUNT_DETAILS
  }, work];
};
function handleUserUpdateCreatePrintableCheckRequest(state, action) {
  let {
    checkAmount,
    checkMemo,
    checkPayeeName
  } = state;

  // Clear error or hint if the customer types in the field
  let {
    amountInputHint,
    memoInputHint,
    recipientInputHint
  } = state.writeCheckInputHints;
  if (action.field === 'amount' && action.value instanceof Money) {
    amountInputHint = InputHintDescription.create();
    checkAmount = action.value;
  }
  if (action.field === 'memo' && typeof action.value === 'string') {
    if (action.value.length >= 40) {
      memoInputHint = InputHintDescription.create({
        helper: `${action.value.length}/50`
      });
    } else {
      memoInputHint = InputHintDescription.create();
    }
    checkMemo = action.value;
  }
  if (action.field === 'payeeName' && typeof action.value === 'string') {
    recipientInputHint = InputHintDescription.create();
    checkPayeeName = action.value;
  }
  const writeCheckInputHints = {
    amountInputHint,
    memoInputHint,
    recipientInputHint
  };
  return [{
    ...state,
    checkAmount,
    checkMemo,
    checkPayeeName,
    writeCheckInputHints
  }, null];
}

/* eslint-disable complexity */
export const update = context => (state, action) => {
  switch (action.type) {
    case Action.Type.ReceivedBalances:
      {
        const {
          response
        } = action;
        const directDepositInstrument = response.balanceInstrument.find(instrument => instrument.type === BalanceInstrumentType.DIRECT_DEPOSIT_ACCOUNT && instrument.state === BalanceInstrumentState.ENABLED);
        const directDepositAccount = assertNonNullable(directDepositInstrument?.directDepositAccount);
        return [{
          ...state,
          directDepositAccount
        }, null];
      }
    case Action.Type.ReceivedDirectDepositAccount:
      {
        const {
          response
        } = action;
        const accountNumber = response.data?.accountNumberFull;
        const routingNumber = assertNonNullable(response.data?.routingNumber);
        const accountNumberSuffix = assertNonNullable(response.data?.accountNumberSuffix);
        const directDepositAccount = {
          routingNumber,
          accountNumberSuffix,
          accountNumber
        };
        return [{
          ...state,
          directDepositAccount
        }, null];
      }
    case Action.Type.ReceivedBillPayData:
      return handleReceivedBillPayData(context)(state, action);
    case Action.Type.ReceivedBootstrapCreatePrintableCheck:
      return handleReceivedBootstrapCreatePrintableCheck(context)(state, action);
    case Action.Type.ReceivedCreatePrintableCheck:
      return handleReceivedCreatePrintableCheck(state, action);
    case Action.Type.ReceivedDownloadPrintableCheck:
      return handleReceivedDownloadPrintableCheck(state, action);
    case Action.Type.ReceivedPrintableCheckAccess:
      return handleReceivedPrintableCheckAccess(context)(state, action);
    case Action.Type.RequestBalances:
      {
        const {
          unitToken
        } = state;
        const work = () => {
          if (context.canBypass2FA) {
            const request = GetCheckingDirectDepositAccountRequest.create({
              unitToken,
              fieldMask: {
                paths: ['data.routing_number', 'data.account_number_suffix']
              }
            });
            return context.getCheckingDirectDepositAccount(request).then(ActionCreator.receivedDirectDepositAccount).catch(error => {
              context.sentry.captureException(error);
              return null;
            });
          }
          const instrumentType = BalanceInstrumentType.DIRECT_DEPOSIT_ACCOUNT;
          const request = ExternalListBalanceInstrumentsRequest.create({
            unitToken,
            instrumentType
          });
          return context.listBalanceInstruments(request).then(ActionCreator.receivedBalances).catch(error => {
            context.sentry.captureException(error);
            return null;
          });
        };
        return [state, work];
      }
    case Action.Type.RequestBillPayData:
      return handleRequestBillPayData(context)(state);
    case Action.Type.RequestBootstrapCreatePrintableCheck:
      return handleBootstrapCreatePrintableCheck(context)(state);
    case Action.Type.RequestCreatePrintableCheck:
      return handleCreatePrintableCheck(context)(state);
    case Action.Type.RequestDownloadPrintableCheck:
      {
        return handleDownloadPrintableCheck(context)(state, action.printableCheckId);
      }
    case Action.Type.RequestPrintableCheckAccess:
      {
        if (context.showPrintableCheck) {
          const work = () => from(getPrintableCheckAccess(context, state));
          return [state, work];
        } else {
          return [state, null];
        }
      }
    case Action.Type.SystemCheckHideEducation:
      {
        const showCheckEducation = !getFromLocalStorage(HIDE_CHECK_EDUCATION_LOCAL_STORAGE_KEY);
        const showWriteACheck = !showCheckEducation;
        return [{
          ...state,
          showCheckEducation,
          showWriteACheck
        }, null];
      }
    case Action.Type.SystemInitialize:
      {
        const {
          canSeeBillPay,
          enabledNewBillPayIntegration,
          showBillPayModal,
          showPrintableCheck,
          showStatements
        } = context;
        const {
          unitToken,
          showPrintableCheckModal
        } = action;
        const newState = {
          ...state,
          canSeeBillPay,
          cardListState: cardListWorkflow.createState({
            unitToken
          }),
          enabledNewBillPayIntegration,
          showBillPayModal,
          showPrintableCheck,
          showPrintableCheckModal,
          showStatements,
          unitToken
        };
        const work = () => {
          return concat(of(ActionCreator.requestBalances(), cardListWorkflow.ActionCreator.requestCardList(), ActionCreator.requestPrintableCheckAccess(), ActionCreator.systemToggleShowAccountNumber(false)));
        };
        return [newState, work];
      }
    case Action.Type.SystemPromptAuthCodeInput:
      {
        const onAuthAttemptComplete = action.onComplete;
        const authModalActive = true;
        return [{
          ...state,
          authModalActive,
          onAuthAttemptComplete
        }, null];
      }
    case Action.Type.SystemReceivedAuthDecision:
      return handleSystemReceivedAuthDecision(context)(state, action);
    case Action.Type.SystemRefreshRecentActivities:
      return handleSystemRefreshRecentActivities(context)(state);
    case Action.Type.SystemToggleAuthModal:
      {
        const authModalActive = action.toggle;
        const authStateResult = authModalActive ? state.authStateResult : StateType.AuthState.NONE;
        const authIsActiveFor = !authModalActive ? StateType.AuthType.NONE : state.authIsActiveFor;
        return [{
          ...state,
          authModalActive,
          authStateResult,
          authIsActiveFor
        }, null];
      }
    case Action.Type.SystemToggleResendButton:
      {
        const isResendButtonActive = action.toggle;
        return [{
          ...state,
          isResendButtonActive
        }, null];
      }
    case Action.Type.SystemToggleShowAccountNumber:
      {
        const showAccountDetails = action.toggle;
        return [{
          ...state,
          showAccountDetails
        }, null];
      }
    case Action.Type.UserChangeAuthCode:
      {
        const authCode = action.value;
        return [{
          ...state,
          authCode
        }, null];
      }
    case Action.Type.UserClickContinueToWriteACheck:
      context.logEvents.track(state.educationConfiguration?.logEvents?.clickContinueLogEvent);
      return [{
        ...state,
        showCheckEducation: false,
        showWriteACheck: true
      }, null];
    case Action.Type.UserClickCreatePrintableCheck:
      context.logEvents.track(state.writeCheckConfiguration?.logEvents?.clickContinueLogEvent);
      return [{
        ...state,
        isLoadingCreateCheck: true
      }, ActionCreator.requestCreatePrintableCheck];
    case Action.Type.UserClickDismissAuthModal:
      {
        const work = () => {
          const complete = state.onAuthAttemptComplete;
          return concat(of(ActionCreator.systemToggleAuthModal(false)), normalizeUpdateAction(complete?.(false) ?? null));
        };
        return [{
          ...state,
          onAuthAttemptComplete: undefined
        }, work];
      }
    case Action.Type.UserClickDismissBillPayModal:
      return [{
        ...state,
        billPayModalActive: false
      }, null];
    case Action.Type.UserClickDismissPrintableCheckBlade:
      {
        const newState = {
          ...state,
          checkPdf: undefined,
          checkPdfFilename: undefined,
          isLoadingDownloadCheck: false,
          isLoadingPrintCheck: false
        };
        return [newState, null];
      }
    case Action.Type.UserClickDismissPrintableCheckModal:
      {
        if (state.printableCheckModalActive) {
          const newState = {
            ...state,
            checkAmount: undefined,
            checkMemo: undefined,
            checkPayeeName: undefined,
            checkPdf: undefined,
            checkPdfFilename: undefined,
            isLoadingCreateCheck: false,
            isLoadingDownloadCheck: false,
            isLoadingPrintCheck: false,
            printableCheckModalActive: false,
            showCheckEducation: true,
            showCheckError: false,
            showWriteACheck: false,
            showCheckSuccess: false
          };
          if (state.showCheckSuccess) {
            return [newState, ActionCreator.systemRefreshRecentActivities];
          } else {
            return [newState, null];
          }
        } else {
          return [state, null];
        }
      }
    case Action.Type.UserClickDownloadCheck:
      {
        const checkIssuedLogEvent = state.checkIssuedConfiguration?.logEvents?.clickDownloadLogEvent;
        if (checkIssuedLogEvent) {
          context.logEvents.track(checkIssuedLogEvent);
        }
        return [{
          ...state,
          isLoadingDownloadCheck: true,
          printableCheckId: action.printableCheckId
        }, () => of(ActionCreator.requestDownloadPrintableCheck(action.printableCheckId))];
      }
    case Action.Type.UserClickHideEducation:
      {
        context.logEvents.track(state.educationConfiguration?.logEvents?.clickDoNotShowAgainLogEvent);
        setToLocalStorage(HIDE_CHECK_EDUCATION_LOCAL_STORAGE_KEY, true, OneTrustConsent.FUNCTIONAL);
        return [{
          ...state,
          showCheckEducation: false,
          showWriteACheck: true
        }, null];
      }
    case Action.Type.UserClickOpenCreatePrintableCheck:
      {
        context.logEvents.track(state.printableCheckAccessConfiguration?.logEvents?.clickLogEvent);
        const work = () => {
          return concat(of(ActionCreator.requestBootstrapCreatePrintableCheck()), of(ActionCreator.systemCheckHideEducation()));
        };
        return [state, work];
      }
    case Action.Type.UserClickPayBills:
      return handleClickPayBills(context)(state);
    case Action.Type.UserClickPayBillsNew:
      return [state, null];
    case Action.Type.UserClickPrintCheck:
      return handleUserClickPrintCheck(context)({
        ...state,
        isLoadingPrintCheck: true
      }, action.printableCheckId);
    case Action.Type.UserClickResendCode:
      return handleUserClickResendCode(context)(state);
    case Action.Type.UserClickShowEducation:
      {
        setToLocalStorage(HIDE_CHECK_EDUCATION_LOCAL_STORAGE_KEY, false, OneTrustConsent.FUNCTIONAL);
        return [{
          ...state,
          showCheckEducation: true,
          showWriteACheck: false
        }, null];
      }
    case Action.Type.UserClickStatementsAndDocuments:
      return [state, null];
    case Action.Type.UserClickSubmitAuthCode:
      return handleUserClickSubmitAuthCode(context)(state);
    case Action.Type.UserClickUploadBill:
      return [state.canSeeBillPay ? {
        ...state,
        billPayModalActive: false
      } : state, null];
    case Action.Type.UserClickWaitlist:
      return [{
        ...state,
        wasWaitlistClicked: true
      }, null];
    case Action.Type.UserToggleAccountDetails:
      return handleUserToggleAccountDetails(context)(state);
    case Action.Type.UserUpdateCreatePrintableCheckRequest:
      return handleUserUpdateCreatePrintableCheckRequest(state, action);
    case Action.Type.SystemDisableToggle:
      context.marketToasts.critical(context.translate('banking.balance.doMoreCard.error'));
      return [{
        ...state,
        isToggleDisabled: true
      }, null];
    case cardListWorkflow.ActionType.CardListReceived:
    case cardListWorkflow.ActionType.CardListRejected:
    case cardListWorkflow.ActionType.CardRelationshipsReceived:
    case cardListWorkflow.ActionType.CardRelationshipsRejected:
    case cardListWorkflow.ActionType.RequestCardList:
    case cardListWorkflow.ActionType.RequestCardRelationships:
    case cardListWorkflow.ActionType.UserClickAddDebitCard:
      {
        const [cardListState, nextActions] = cardListWorkflow.update({
          getCardList: context.getCardList,
          retrieveCardRelationships: context.retrieveCardRelationships,
          sentry: context.sentry
        })(state.cardListState, action);
        return [{
          ...state,
          cardListState
        }, nextActions];
      }
  }
};
/* eslint-enable complexity */